
import { useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UseSessionInitializationProps {
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useSessionInitialization = ({ 
  setUser, 
  setSession, 
  setLoading 
}: UseSessionInitializationProps) => {
  useEffect(() => {
    // Initialize session from Supabase directly
    const initializeSession = async () => {
      try {
        // Get the current session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session initialization error:', error);
        } else if (session) {
          setSession(session);
          setUser(session.user);
        }
      } catch (error) {
        console.error('Session initialization exception:', error);
      } finally {
        setLoading(false);
      }
    };

    // Set up auth state listener for client-side changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        
        // Update local state
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle specific events
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
        }
        
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed, clearing session');
          setUser(null);
          setSession(null);
        }
        
        // Ensure loading is false after auth state changes
        setLoading(false);
      }
    );

    // Initialize session on mount
    initializeSession();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setSession, setLoading]);
};
